// src/pages/buyer/ManufacturingOrderPage.jsx
// Phase 5 — M5: Buyer manufacturing / CMT order management

import { useEffect, useState } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const STATUS_FLOW = ['draft','submitted','quoted','accepted','in_production','qc','dispatched','delivered']
const STATUS_LABELS = { draft:'Draft', submitted:'Submitted', quoted:'Quote Received', accepted:'Accepted', in_production:'In Production', qc:'Quality Check', dispatched:'Dispatched', delivered:'Delivered', cancelled:'Cancelled' }
const STATUS_COLORS = {
  draft:         '#9CA3AF',
  submitted:     '#3B82F6',
  quoted:        '#F59E0B',
  accepted:      '#8B5CF6',
  in_production: ORG,
  qc:            '#06B6D4',
  dispatched:    '#6366F1',
  delivered:     '#10B981',
  cancelled:     '#EF4444',
}

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] ?? GREY
  return (
    <span style={{ background: color+'22', color, padding: '3px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 700 }}>
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

function ProgressBar({ status }) {
  const idx  = STATUS_FLOW.indexOf(status)
  const pct  = idx < 0 ? 0 : Math.round(((idx + 1) / STATUS_FLOW.length) * 100)
  const color = STATUS_COLORS[status] ?? GREY
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.75rem', color: GREY }}>
        <span>{STATUS_LABELS[STATUS_FLOW[0]]}</span>
        <span style={{ color }}>{pct}% complete</span>
        <span>{STATUS_LABELS[STATUS_FLOW[STATUS_FLOW.length - 1]]}</span>
      </div>
      <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  )
}

export default function ManufacturingOrderPage() {
  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [tab, setTab]           = useState('orders')
  const [expanded, setExpanded] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ supplier_id: '', product_name: '', order_type: 'cmt', quantity: '', unit: 'metre', specifications: '', required_by: '' })
  const [saving, setSaving]     = useState(false)
  const [acceptModal, setAcceptModal] = useState(null) // orderId
  const [advancePaid, setAdvancePaid] = useState('')

  const load = () => {
    setLoading(true)
    api('/buyer/manufacturing').then(r => {
      setData(r.data ?? r)
      setLoading(false)
    })
  }

  useEffect(() => { load() }, [])

  const place = async () => {
    setSaving(true)
    await api('/buyer/manufacturing', { method: 'POST', body: JSON.stringify(form) })
    setSaving(false); setShowModal(false)
    setForm({ supplier_id: '', product_name: '', order_type: 'cmt', quantity: '', unit: 'metre', specifications: '', required_by: '' })
    load()
  }

  const accept = async () => {
    setSaving(true)
    await api(`/buyer/manufacturing/${acceptModal}/accept`, { method: 'POST', body: JSON.stringify({ advance_paid: advancePaid || 0 }) })
    setSaving(false); setAcceptModal(null); setAdvancePaid(''); load()
  }

  const cancel = async (id) => {
    if (!window.confirm('Cancel this manufacturing order?')) return
    await api(`/buyer/manufacturing/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason: 'Cancelled by buyer' }) })
    load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading manufacturing orders…</div>

  const orders = data?.orders ?? []
  const active = data?.active  ?? 0

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Manufacturing Orders</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Place CMT and full-package orders with textile manufacturers</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem' }}>
          + New Order
        </button>
      </div>

      {/* Overview bar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Active Orders', value: active, color: PINK },
          { label: 'Total Orders',  value: orders.length, color: DARK },
          { label: 'In Production', value: orders.filter(o => o.status === 'in_production').length, color: ORG },
          { label: 'Delivered',     value: orders.filter(o => o.status === 'delivered').length, color: '#10B981' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 16, padding: '18px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontWeight: 600, color: DARK, fontSize: '0.88rem', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Orders */}
      {orders.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 56, textAlign: 'center', color: GREY, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🏭</div>
          <div style={{ fontWeight: 700, color: DARK, marginBottom: 4 }}>No manufacturing orders yet</div>
          <div style={{ fontSize: '0.88rem' }}>Place a CMT or full-package order to get started</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {orders.map(order => {
            const isOpen = expanded === order.id
            const canAccept  = order.status === 'quoted'
            const canCancel  = !['delivered','dispatched','cancelled'].includes(order.status)
            return (
              <div key={order.id} style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                {/* Header row */}
                <div
                  onClick={() => setExpanded(isOpen ? null : order.id)}
                  style={{ padding: '18px 24px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 120px', alignItems: 'center', gap: 12 }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: DARK, fontSize: '0.95rem' }}>{order.product_name}</div>
                    <div style={{ color: GREY, fontSize: '0.78rem', marginTop: 2 }}>
                      {order.order_number} &nbsp;·&nbsp; {order.order_type?.replace('_',' ')} &nbsp;·&nbsp;
                      via {order.supplier?.business_name ?? order.supplier?.name ?? `Supplier #${order.supplier_id}`}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: DARK }}>{order.quantity?.toLocaleString()} {order.unit}s</div>
                  <div style={{ fontWeight: 700, color: DARK }}>₹{parseFloat(order.total_amount || 0).toLocaleString('en-IN')}</div>
                  <div><StatusPill status={order.status} /></div>
                  <div style={{ textAlign: 'right', color: GREY, fontSize: '0.8rem' }}>
                    {new Date(order.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                    <span style={{ marginLeft: 8, color: PINK }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded */}
                {isOpen && (
                  <div style={{ padding: '0 24px 24px', borderTop: '1px solid #F3F4F6' }}>
                    <div style={{ padding: '16px 0' }}>
                      <ProgressBar status={order.status} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.88rem' }}>Order Details</div>
                        {[
                          ['Required By', order.required_by ? new Date(order.required_by).toLocaleDateString('en-IN') : '—'],
                          ['Est. Completion', order.estimated_completion ? new Date(order.estimated_completion).toLocaleDateString('en-IN') : 'Awaiting quote'],
                          ['Unit Price', order.unit_price ? `₹${parseFloat(order.unit_price).toLocaleString('en-IN')}` : 'Awaiting quote'],
                          ['Advance Paid', `₹${parseFloat(order.advance_paid || 0).toLocaleString('en-IN')}`],
                        ].map(([l,v]) => (
                          <div key={l} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.84rem' }}>
                            <span style={{ color: GREY, minWidth: 130 }}>{l}:</span>
                            <span style={{ color: DARK, fontWeight: 600 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: DARK, marginBottom: 10, fontSize: '0.88rem' }}>Specifications</div>
                        <div style={{ background: GR_LT, borderRadius: 10, padding: '12px 14px', fontSize: '0.83rem', color: '#374151', lineHeight: 1.6, maxHeight: 100, overflowY: 'auto' }}>
                          {order.specifications}
                        </div>
                      </div>
                    </div>
                    {/* Actions */}
                    {(canAccept || canCancel) && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                        {canAccept && (
                          <button onClick={() => setAcceptModal(order.id)} style={{ padding: '8px 18px', borderRadius: 9, border: 'none', background: '#10B981', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
                            ✓ Accept Quote
                          </button>
                        )}
                        {canCancel && (
                          <button onClick={() => cancel(order.id)} style={{ padding: '8px 18px', borderRadius: 9, border: '1.5px solid #EF4444', background: '#fff', color: '#EF4444', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
                            Cancel Order
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Place Order Modal ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 520, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>New Manufacturing Order</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Supplier ID *</label>
                  <input type="number" value={form.supplier_id} onChange={e => setForm(f => ({...f,supplier_id:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Order Type</label>
                  <select value={form.order_type} onChange={e => setForm(f => ({...f,order_type:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                    {[['cmt','CMT (Cut-Make-Trim)'],['full_package','Full Package'],['private_label','Private Label']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Product Name *</label>
                <input value={form.product_name} onChange={e => setForm(f => ({...f,product_name:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Quantity *</label>
                  <input type="number" value={form.quantity} onChange={e => setForm(f => ({...f,quantity:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Unit</label>
                  <select value={form.unit} onChange={e => setForm(f => ({...f,unit:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                    {['metre','piece','kg','yard'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Required By</label>
                  <input type="date" value={form.required_by} onChange={e => setForm(f => ({...f,required_by:e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Specifications / Tech Pack Notes *</label>
                <textarea rows={4} value={form.specifications} onChange={e => setForm(f => ({...f,specifications:e.target.value}))} placeholder="Describe fabric specs, colours, finish, construction details, or attach tech pack reference…" style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={place} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Submitting…' : 'Submit Order'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Accept Quote Modal ── */}
      {acceptModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 380, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 16px' }}>Accept Quote</h2>
            <p style={{ color: GREY, fontSize: '0.87rem', marginBottom: 16 }}>Confirm acceptance of the supplier's quote. You may pay an advance now.</p>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Advance Payment (₹)</label>
              <input type="number" value={advancePaid} onChange={e => setAdvancePaid(e.target.value)} placeholder="0" style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={() => setAcceptModal(null)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={accept} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: '#10B981', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Accepting…' : 'Confirm & Accept'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
